import React, { useMemo } from "react";
import { Select, Box, MenuItem, Typography, Stack, DateTimeStandard } from '@curves/ui-base';
import clsx from "clsx";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { parseDate } from "@Util/moment";
import { isEmpty } from "@Util/utils";

/**
 * MeasureDateCard - 量身日期卡片組件
 *
 * 顯示最近量身日以及對比量身日。
 * 若傳入 measureOptions + onCompareIDChange，對比側顯示為可選擇的下拉選單。
 *
 * @component
 * @param {Object}   props
 * @param {string}   [props.className]
 * @param {string}   props.recentMeasureDate        - 最近量身日
 * @param {string}   [props.deltaMeasureDate]       - 對比量身日（固定顯示用）
 * @param {Array}    [props.measureOptions]          - 量身日期選項 [{ text, value }]
 * @param {string}   [props.compareID]               - 目前選取的 compareID
 * @param {Function} [props.onCompareIDChange]       - 選擇回調 (event) => void
 */
const MeasureDateCard = ({ className, recentMeasureDate, deltaMeasureDate, measureOptions, compareID, onCompareIDChange }) => {

	const isSelectMode = !isEmpty(measureOptions) && typeof onCompareIDChange === "function";
    
	const deltaSection = useMemo(() => {
		if (isSelectMode) {
			return (
				<>
					<Box className="arrow">
						<CompareArrowsIcon className="arrow-icon" />
					</Box>
				<Box className="item">
						<Typography className="label">對比量身日</Typography>
						<Box className="value value--stacked measure-date-card__select-wrapper">
							<Select
								value={compareID ?? ""}
								onChange={onCompareIDChange}
								displayEmpty
								renderValue={(val) => {
									const selected = measureOptions.find(opt => String(opt.value) === String(val));
									if (!selected) return val;
									const [date, time] = selected.text.split(" ");
									return (
										<>
											<Box>{date}</Box>
											<Box className="font-color-4">{time}</Box>
										</>
									);
								}}
							>
								{measureOptions.map(opt => (
									<MenuItem key={opt.value} value={opt.value}>
										{opt.text}
									</MenuItem>
								))}
							</Select>
						</Box>
					</Box>
				</>
			);
		}

		if (!isEmpty(deltaMeasureDate)) {
			return (
				<>
					<Box className="arrow">
						<CompareArrowsIcon className="arrow-icon" />
					</Box>
					<Box className="item">
						<Typography className="label">對比量身日</Typography>
						<Box>
							<Typography component="div" className="value value--stacked">
								<Box>{parseDate(deltaMeasureDate, DateTimeStandard.DisplayDateBySlash)}</Box>
								<Box className={"font-color-4"}>{parseDate(deltaMeasureDate, DateTimeStandard.DisplayTime)}</Box>
							</Typography>
						</Box>
					</Box>
				</>
			);
		}

		return null;
		// eslint-disable-next-line
	}, [deltaMeasureDate, measureOptions, compareID, isSelectMode]);

	return (
		<Box className={clsx("measure-date-card", className)}>
			<Stack className="comparison-box" direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
				<Box className="item">
					<Typography className="label">最近量身日</Typography>
					<Typography component="div" className="value value--stacked">
						<Box>{parseDate(recentMeasureDate, DateTimeStandard.DisplayDateBySlash)}</Box>
						<Box className={"font-color-4"}>{parseDate(recentMeasureDate, DateTimeStandard.DisplayTime)}</Box>
					</Typography>
				</Box>
				{deltaSection}
			</Stack>
		</Box>
	);
};

export default MeasureDateCard;
